import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useCurrentProgramSlug, useCurrentCampaign } from '@state/useCurrentProgramSlug';

/**
 * Guards a program-scoped route against per-campaign page visibility.
 *
 * When the current campaign lists `pageKey` in its Setup Summary `hidden_pages`
 * (Campaign.hiddenPages) — e.g. a fulfillment-only campaign like BP Cuffs that
 * hides Clinical / Survey & Sentiment / Interactive & Predictive — a direct
 * visit (bookmark, typed URL) redirects to that program's Flight Deck rather
 * than rendering an empty, irrelevant page. Defense-in-depth behind the nav,
 * which already drops the link (navStructure.buildNavGroups).
 *
 * Keys match NavItem.key in navStructure.ts.
 */
export function HideableRoute({ pageKey, children }: { pageKey: string; children: ReactNode }) {
  const slug = useCurrentProgramSlug();
  const campaign = useCurrentCampaign();
  const hidden = campaign?.hiddenPages ?? [];
  // Board-only campaigns keep their Flight Deck card but expose NO
  // program-scoped pages — any direct URL bounces to the board.
  if (campaign?.boardOnly || hidden.includes(pageKey)) {
    return <Navigate to={`/${slug}/flight-deck`} replace />;
  }
  return <>{children}</>;
}
